import { useReducer, useEffect } from 'react';
import { phoneTagsConfig } from '../../config/phone-config';
import { getUrlParams } from '@lux/helpers';
import slugify from '../../helpers/slugify';
import { CATALOGUE } from '../../constants';

export const FILTER_ACTIONS = {
  SET_BRANDS: 'SET_BRANDS',
  SET_CATEGORIES: 'SET_CATEGORIES',
  SET_IPP_OPTIONS: 'SET_IPP_OPTIONS',
  RESET: 'RESET',
  CUSTOM: 'CUSTOM'
};

const { QUERYSTRING_TYPE } = CATALOGUE;

export const initialState = {
  categoriesFilter: {},
  brandsFilter: {},
  ippFilter: {}
};

export const reducer = (state, action) => {
  switch (action.type) {
    case FILTER_ACTIONS.SET_BRANDS:
      return { ...state, brandsFilter: action.payload };
    case FILTER_ACTIONS.SET_CATEGORIES:
      return { ...state, categoriesFilter: action.payload };
    case FILTER_ACTIONS.SET_IPP_OPTIONS:
      return { ...state, ippFilter: action.payload };
    case FILTER_ACTIONS.CUSTOM:
      return { ...state, ...action.payload };
    case FILTER_ACTIONS.RESET:
      return initialState;
    default:
      return state;
  }
};

export const normalizePhone = phones => {
  let allIPPOptions = [];

  const phonesBySlug = phones.reduce((acc, cur) => {
    const ipp = cur.tags.filter(tag =>
      tag.includes(phoneTagsConfig['ipp'].tagName)
    );
    allIPPOptions = [...allIPPOptions, ...ipp];

    const category = cur.tags.filter(tag =>
      tag.includes(phoneTagsConfig['product-category'].tagName)
    );

    return {
      ...acc,
      [cur.slug]: {
        ...cur,
        ipp,
        category
      }
    };
  }, {});

  allIPPOptions = Array.from(new Set(allIPPOptions));

  return {
    phonesBySlug,
    allIPPOptions
  };
};

export const createParamItem = (item, key, data, paramItems) => {
  if (Object.keys(item).length) {
    let checkedItems = [];
    Object.keys(item).forEach(value => {
      if (item[value]) {
        const matched = data[key]?.find(itm => itm.value === value);

        if (matched) {
          checkedItems.push(matched.slug);
        }
      }
    });
    if (checkedItems.length) {
      paramItems.push(`${key}=${checkedItems.join(',')}`);
    }
  }
};

export const usePhoneCatalogFilter = ({
  phones,
  allBrands,
  allInstalmentOptions,
  allCategories,
  cisPlanRates,
  onFilterChange,
  defaultFilter,
  location
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { categoriesFilter, brandsFilter, ippFilter } = state;
  const { phonesBySlug, allIPPOptions } = normalizePhone(phones);
  const immutablePhoneList = Object.keys(phonesBySlug);
  let phoneList = [...immutablePhoneList];
  let filterList = [...immutablePhoneList];

  const filterSectionData = {
    [QUERYSTRING_TYPE.BRAND]: allBrands.map(item => ({
      text: item,
      value: item,
      slug: slugify(item)
    })),
    [QUERYSTRING_TYPE.CATEGORY]: allCategories.map(item => ({
      text: item.text,
      value: item.value,
      slug: slugify(item.text)
    })),
    [QUERYSTRING_TYPE.IPP]: allInstalmentOptions?.map(item => ({
      text: item.text,
      value: item.value,
      slug: slugify(item.text)
    }))
  };

  /* update filters based on querystring */
  useEffect(() => {
    const filteredData = {};
    Object.entries(getUrlParams()).forEach(([key, value]) => {
      if (filterSectionData[key]) {
        const values = value.split(',').filter(Boolean);
        values.forEach(val => {
          const matched = filterSectionData[key]?.find(
            item => item.slug === val
          );
          if (matched) {
            filteredData[key] = {
              ...filteredData[key],
              [matched.value]: true
            };
          }
        });
      }
    });

    if (Object.keys(filteredData).length) {
      dispatch({
        type: FILTER_ACTIONS.CUSTOM,
        payload: {
          brandsFilter: filteredData[QUERYSTRING_TYPE.BRAND] || {},
          categoriesFilter: filteredData[QUERYSTRING_TYPE.CATEGORY] || {},
          ippFilter: filteredData[QUERYSTRING_TYPE.IPP] || {}
        }
      });
    }
  }, []);

  /* update displayed phones */
  useEffect(() => {
    onFilterChange(phoneList, ippFilter);
    const urlParams = getUrlParams();
    let paramItems = [];
    const pushSessionTokenParams = () => {
      if (urlParams.sessionToken) {
        paramItems.push(
          `${QUERYSTRING_TYPE.SESSIONTOKEN}=${urlParams.sessionToken}`
        );
        if (!urlParams.plan && defaultFilter?.planName) {
          const defaultPlan = slugify(defaultFilter.planName);
          paramItems.push(`${QUERYSTRING_TYPE.PLAN}=${defaultPlan}`);
        }
      }
    };

    pushSessionTokenParams();

    if (urlParams.plan) {
      paramItems.push(`${QUERYSTRING_TYPE.PLAN}=${urlParams.plan}`);
    }

    const setUtmParams = utmParams => {
      utmParams?.utm_source &&
        paramItems.push(
          `${QUERYSTRING_TYPE.UTM_SOURCE}=${utmParams?.utm_source}`
        );

      utmParams?.utm_medium &&
        paramItems.push(
          `${QUERYSTRING_TYPE.UTM_MEDIUM}=${utmParams?.utm_medium}`
        );

      utmParams?.utm_campaign &&
        paramItems.push(
          `${QUERYSTRING_TYPE.UTM_CAMPAIGN}=${utmParams?.utm_campaign}`
        );

      utmParams?.utm_term &&
        paramItems.push(`${QUERYSTRING_TYPE.UTM_TERM}=${utmParams?.utm_term}`);

      utmParams?.utm_content &&
        paramItems.push(
          `${QUERYSTRING_TYPE.UTM_CONTENT}=${utmParams?.utm_content}`
        );

      utmParams?.utm_id &&
        paramItems.push(`${QUERYSTRING_TYPE.UTM_ID}=${utmParams?.utm_id}`);
    };

    const locationParams = location && getUrlParams(location.search);
    if (locationParams?.utm_source) {
      setUtmParams(locationParams);
    }

    createParamItem(
      categoriesFilter,
      QUERYSTRING_TYPE.CATEGORY,
      filterSectionData,
      paramItems
    );

    createParamItem(
      brandsFilter,
      QUERYSTRING_TYPE.BRAND,
      filterSectionData,
      paramItems
    );

    createParamItem(
      ippFilter,
      QUERYSTRING_TYPE.IPP,
      filterSectionData,
      paramItems
    );

    if (paramItems.length > 1) {
      window.history.replaceState(null, null, `?${paramItems.join('&')}`);
    }
  }, [ippFilter, brandsFilter, categoriesFilter]);

  const handleChange = (type = '', value) => {
    dispatch({ type: FILTER_ACTIONS[type], payload: value });
  };

  const brandValues = Object.keys(brandsFilter).filter(
    key => brandsFilter[key]
  );

  if (brandValues.length > 0) {
    phoneList = phoneList.filter(key =>
      brandValues.includes(phonesBySlug[key].brand)
    );
  }

  /* selected ipp options */
  const iPPValues = Object.keys(ippFilter).filter(key => ippFilter[key]);

  if (iPPValues.length > 0) {
    /* get display phones */
    phoneList = phoneList.filter(key =>
      iPPValues.some(ipp => phonesBySlug[key].ipp.includes(ipp))
    );
    /* get brands that compatible to selected ipp */
    filterList = filterList.filter(key =>
      iPPValues.some(ipp => phonesBySlug[key].ipp.includes(ipp))
    );
  }

  /* selected categories */
  const categoriesValues = Object.keys(categoriesFilter).filter(
    key => categoriesFilter[key]
  );
  if (categoriesValues.length > 0) {
    /* get display phones */
    phoneList = phoneList.filter(key =>
      categoriesValues.some(category =>
        phonesBySlug[key].category.includes(category)
      )
    );
    /* get brands that compatible to selected category */
    filterList = filterList.filter(key =>
      categoriesValues.some(category =>
        phonesBySlug[key].category.includes(category)
      )
    );
  }

  let availableBrand = filterList.map(key => phonesBySlug[key].brand);
  availableBrand = Array.from(new Set(availableBrand));

  let availableCategory = [];
  let availableIppOptions = [];

  phoneList.forEach(key => {
    availableCategory = [...availableCategory, ...phonesBySlug[key].category];
    availableIppOptions = [...availableIppOptions, ...phonesBySlug[key].ipp];
  });
  availableCategory = Array.from(new Set(availableCategory));
  availableIppOptions = Array.from(new Set(availableIppOptions));

  const brandsList = allBrands
    .map(brand => ({
      text: brand,
      value: brand,
      disabled:
        (categoriesValues.length > 0 || iPPValues.length > 0) &&
        !availableBrand.includes(brand)
    }))
    .sort((a, b) => a.text.localeCompare(b.text));

  const ippOptionList =
    allInstalmentOptions &&
    allInstalmentOptions
      /* Get list of phones that have ipp */
      .filter(ipp => allIPPOptions.includes(ipp.value))
      .map(ipp => {
        return {
          ...ipp,
          disabled: !availableIppOptions.includes(ipp.value)
        };
      });

  const categoriesList = allCategories.map(category => {
    return {
      ...category,
      disabled: !availableCategory.includes(category.value)
    };
  });

  return {
    categoriesFilter,
    brandsFilter,
    ippFilter,
    setFilter: handleChange,
    categoriesList,
    brandsList,
    ippOptionList
  };
};

export default usePhoneCatalogFilter;
